import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from '../../../utils/classNames';
import IconButton from '../../atoms/IconButton';
import ChevronIcon from '../../atoms/icons/ChevronIcon';
import EventCalendarDay from '../EventCalendarDay';
import { getCellKey } from '../../../utils/eventsCalendar';
import './index.scss';

const LEGEND_ITEMS = [
    { key: 'going', dotVar: 'var(--s-going-fg)' },
    { key: 'interested', dotVar: 'var(--s-interest-fg)' },
    { key: 'pending', dotVar: 'var(--s-pending-fg)' },
    { key: 'cancel', dotVar: 'var(--s-cancel-fg)' },
    { key: 'decline', dotVar: 'var(--s-decline-fg)' },
    { key: 'complete', dotVar: 'var(--s-complete-fg)' },
];

export default function CalendarMonthGrid({
    monthDate,
    days,
    eventsByDay,
    onEventClick,
    onDateSelect,
    onEventDrop,
    canSelectDates = false,
    canMutateEvents = false,
    onPrev,
    onNext,
    onToday,
    className = '',
}) {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language || 'en-US';

    const monthHeading = useMemo(() => {
        try {
            return new Intl.DateTimeFormat(locale, {
                month: 'long',
                year: 'numeric',
            }).format(monthDate);
        } catch (error) {
            return '';
        }
    }, [locale, monthDate]);

    const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

    return (
        <section
            className={classNames('calendar-month-grid', className)}
            aria-label={t('eventsPage.calendar.monthLabel', { month: monthHeading })}
        >
            <header className="calendar-month-grid__head">
                <div className="calendar-month-grid__month">
                    <h3 className="calendar-month-grid__title">{monthHeading}</h3>
                </div>
                <div className="calendar-month-grid__nav">
                    <IconButton
                        icon={<ChevronIcon direction="left" />}
                        label={t('eventsPage.calendar.prevMonth')}
                        variant="glass"
                        size={34}
                        onClick={onPrev}
                    />
                    <button
                        type="button"
                        className="calendar-month-grid__today-btn"
                        onClick={onToday}
                    >
                        {t('eventsPage.calendar.today')}
                    </button>
                    <IconButton
                        icon={<ChevronIcon direction="right" />}
                        label={t('eventsPage.calendar.nextMonth')}
                        variant="glass"
                        size={34}
                        onClick={onNext}
                    />
                </div>
            </header>

            <div className="calendar-month-grid__weekdays" aria-hidden="true">
                {weekdayKeys.map((key) => (
                    <span key={key}>{t(`eventsPage.calendar.weekdays.${key}`)}</span>
                ))}
            </div>

            <div className="calendar-month-grid__days">
                {days.map((row, rowIndex) => (
                    row.map((cell) => {
                        const key = getCellKey(cell.date);
                        const events = eventsByDay?.get?.(key) || [];
                        return (
                            <EventCalendarDay
                                key={`${rowIndex}-${key}`}
                                date={cell.date}
                                events={events}
                                isCurrentMonth={cell.isCurrentMonth}
                                isToday={cell.isToday}
                                onEventClick={onEventClick}
                                onDateSelect={onDateSelect}
                                onEventDrop={onEventDrop}
                                canSelectDate={canSelectDates}
                                canMutateEvents={canMutateEvents}
                            />
                        );
                    })
                ))}
            </div>

            <div className="calendar-month-grid__legend">
                {LEGEND_ITEMS.map((entry) => (
                    <span key={entry.key} className="calendar-month-grid__legend-item">
                        <span
                            className="calendar-month-grid__legend-dot"
                            style={{ background: entry.dotVar }}
                            aria-hidden="true"
                        />
                        {t(`eventsPage.calendar.legend.${entry.key}`)}
                    </span>
                ))}
            </div>
        </section>
    );
}

CalendarMonthGrid.propTypes = {
    monthDate: PropTypes.instanceOf(Date).isRequired,
    days: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.instanceOf(Date),
        isCurrentMonth: PropTypes.bool,
        isToday: PropTypes.bool,
    }))).isRequired,
    eventsByDay: PropTypes.instanceOf(Map),
    onEventClick: PropTypes.func,
    onDateSelect: PropTypes.func,
    onEventDrop: PropTypes.func,
    canSelectDates: PropTypes.bool,
    canMutateEvents: PropTypes.bool,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    onToday: PropTypes.func,
    className: PropTypes.string,
};
